/**
 * Project Bobcat — whether a Knowledge Agent's knowledge base finished
 * setting up.
 *
 * Adding files to an existing solution uploads them, but the backend doesn't
 * always create the search index (corpus) and write `live_corpus_id` onto the
 * RAG search node. Without a limit, Publish would wait on that forever. So an
 * upload is given `KNOWLEDGE_SETUP_TIMEOUT_MS` to land; past that, the setup
 * is treated as not finished, and the user can skip the search instead.
 *
 * When the upload happened is kept on the RAG search node's settings
 * (`knowledgeUploadAt`), so it survives reloads. A solution with no record
 * (files added before this existed, or at creation) is timed from when this
 * session first saw it waiting.
 */
import {
  isUnprovisionedRagNode,
  type SkipCorpusRow,
} from './skip-knowledge-corpus';

/** How long an upload gets to produce a search index before we call it stuck. */
export const KNOWLEDGE_SETUP_TIMEOUT_MS = 3 * 60 * 1000;

export type KnowledgeSetupState =
  /** No RAG search is waiting on a corpus. */
  | 'ready'
  /** A RAG search waits, and no knowledge files were ever added. */
  | 'no-knowledge-base'
  /** Files were added and the index is still within its time to appear. */
  | 'provisioning'
  /** Files were added, but the index never appeared. */
  | 'stalled';

type RowWithSettings = SkipCorpusRow & {
  settings?: Record<string, unknown> | null;
};

/** When knowledge was last uploaded for the waiting RAG search, if recorded. */
export const readKnowledgeUploadAt = (
  rows: RowWithSettings[]
): number | null => {
  let latest: number | null = null;
  rows.forEach((row) => {
    if (!isUnprovisionedRagNode(row)) return;
    const raw = row.settings?.knowledgeUploadAt;
    const at = typeof raw === 'string' ? Date.parse(raw) : NaN;
    if (Number.isFinite(at) && (latest === null || at > latest)) latest = at;
  });
  return latest;
};

/**
 * The waiting RAG search rows with `knowledgeUploadAt` set to `at`, ready to
 * save. Empty when nothing is waiting.
 */
export const markKnowledgeUploadRows = <R extends RowWithSettings>(
  rows: R[],
  at: Date
): R[] =>
  rows.filter(isUnprovisionedRagNode).map((row) => ({
    ...row,
    settings: { ...(row.settings ?? {}), knowledgeUploadAt: at.toISOString() },
  }));

export const knowledgeSetupState = ({
  unprovisioned,
  hasFiles,
  uploadAt,
  firstSeenAt,
  now,
  timeoutMs = KNOWLEDGE_SETUP_TIMEOUT_MS,
}: {
  /** A RAG search is waiting on an empty corpus. */
  unprovisioned: boolean;
  /** The solution has knowledge files. */
  hasFiles: boolean;
  /** When files were last uploaded, if recorded (ms). */
  uploadAt: number | null;
  /** When this session first saw the search waiting (ms). */
  firstSeenAt: number | null;
  now: number;
  timeoutMs?: number;
}): KnowledgeSetupState => {
  if (!unprovisioned) return 'ready';
  if (!hasFiles) return 'no-knowledge-base';
  const startedAt = uploadAt ?? firstSeenAt ?? now;
  return now - startedAt >= timeoutMs ? 'stalled' : 'provisioning';
};

/** States in which Publish is held by the knowledge search with a clear reason. */
export const knowledgeSetupBlocksPublish = (state: KnowledgeSetupState) =>
  state === 'no-knowledge-base' || state === 'stalled';
